import React, { useState, useRef, useEffect } from 'react';
import { FaPalette, FaSun, FaMoon, FaCheck } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';

const ThemeCustomizer = ({ variant = 'header' }) => {
  const isAuth = variant === 'auth';
  const triggerClass = isAuth
    ? 'p-2.5 rounded-xl bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-700'
    : 'p-2.5 rounded-xl bg-white/10 text-white dark:bg-neutral-800 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-neutral-700';
  const { isDarkMode, toggleDarkMode, colorTheme, setColorTheme, availableThemes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative transition-all duration-300 group ${triggerClass}`}
        title="Customize Theme"
      >
        <FaPalette className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
        <span
          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-black dark:border-neutral-900"
          style={{ background: `linear-gradient(135deg, rgb(var(--primary-500)), rgb(var(--secondary-500)))` }}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 sm:w-80 glass rounded-2xl shadow-3d border border-white/20 dark:border-neutral-700 overflow-hidden animate-fade-in z-50">
          <div className="px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
            <h3 className="font-semibold text-sm">Appearance Settings</h3>
            <p className="text-xs text-white/70 mt-0.5">Customize your dashboard experience</p>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Color Mode
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => !isDarkMode || toggleDarkMode()}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    !isDarkMode
                      ? 'bg-primary-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-700'
                  }`}
                >
                  <FaSun className="w-4 h-4" />
                  Light
                </button>
                <button
                  onClick={() => isDarkMode || toggleDarkMode()}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isDarkMode
                      ? 'bg-black text-white shadow-lg ring-1 ring-neutral-700'
                      : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-700'
                  }`}
                >
                  <FaMoon className="w-4 h-4" />
                  Dark
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Color Theme
              </p>
              <div className="grid grid-cols-2 gap-2">
                {availableThemes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => setColorTheme(theme.id)}
                    className={`relative flex items-center gap-2.5 p-2.5 rounded-xl text-left transition-all ${
                      colorTheme === theme.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500'
                        : 'bg-gray-50 dark:bg-neutral-800 hover:bg-gray-100 dark:hover:bg-neutral-700'
                    }`}
                  >
                    <div className="flex -space-x-1 shrink-0">
                      {theme.preview.map((color, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded-full border-2 border-white dark:border-neutral-900 shadow-sm"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                        {theme.name}
                      </p>
                    </div>
                    {colorTheme === theme.id && (
                      <FaCheck className="w-3 h-3 text-primary-500 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeCustomizer;
